import { Box, Button, Flex, Text, Img, keyframes } from '@chakra-ui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import routes from 'constants/routes';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';
import { selectUser, setUser } from 'store/slices/authSlice';
import { event, EVENTS } from 'utils/analytics';
import { setDisplayingStatus, setInitialIndex } from 'store/slices/paywallSlice';
import { useGetWeeklyRaffleQuery } from 'store/services/api';
import Preloader from 'components/Preloader';
import formatEndTime from 'utils/formatEndTime';

dayjs.extend(utc);
dayjs.extend(timezone);

const glowiAnimation = keyframes`
  0% {
    filter: blur(0px);
    transform: scale(1);
  }
  50% {
    filter: blur(30px);
    transform: scale(1.1);
  }
  100% {
    filter: blur(50px);
    transform: scale(0.95);
  }
`;

const RaffleBanner = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  const { data: weeklyRaffle, isLoading } = useGetWeeklyRaffleQuery();

  return (
    <Flex
      backgroundColor="rgba(0, 0, 0, 0.15)"
      borderRadius="10px"
      mt="24px"
      position="relative"
      justifyContent="space-between"
      overflow="hidden"
      flexDirection="column"
      border="1px solid rgba(255, 255, 255, 0.06)"
    >
      {isLoading ? (
        <Preloader />
      ) : (
        <>
          <Flex
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            filter="blur(0px)"
            animation={`${glowiAnimation} 1.6s infinite alternate ease-in-out 0.3s`}
            justifyContent="center"
            alignItems="center"
          >
            <svg width="345" height="360" viewBox="0 0 345 360" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.8" filter="url(#filter0_f_1769_6792)">
                <path
                  d="M173 10L187.364 116.066L246.326 26.7363L213.248 128.531L305.13 73.6302L231.16 150.992L337.763 141.394L237.552 179L337.763 216.606L231.16 207.008L305.13 284.37L213.248 229.469L246.326 331.264L187.364 241.934L173 348L158.636 241.934L99.6736 331.264L132.752 229.469L40.8705 284.37L114.84 207.008L8.23718 216.606L108.448 179L8.23718 141.394L114.84 150.992L40.8705 73.6302L132.752 128.531L99.6736 26.7363L158.636 116.066L173 10Z"
                  fill="#44A5FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_1769_6792"
                  x="-31.7627"
                  y="-30"
                  width="409.525"
                  height="418"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                  <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur_1769_6792" />
                </filter>
              </defs>
            </svg>
          </Flex>
          <Flex flexDirection="column" alignItems="center" gap="8px" pt="24px" mb="-20px" textAlign="center" zIndex="2">
            <Text
              fontSize="22px"
              lineHeight="1.1"
              fontWeight="400"
              color="var(--chakra-colors-white)"
              fontFamily="'Fugaz One', cursive"
              textTransform="uppercase"
            >
              {weeklyRaffle?.results?.[0]?.name ? weeklyRaffle?.results?.[0]?.name : '--'}
            </Text>
            <Text
              fontSize="14px"
              lineHeight="1.2"
              fontWeight="400"
              color="var(--chakra-colors-white)"
              fontFamily="'Fugaz One', cursive"
              textTransform="uppercase"
            >
              WINNER ANNOUNCED ON 5CD IG <br />
              <Box as="span" color="var(--chakra-colors-blue)">
                {weeklyRaffle?.results?.[0]?.end_time ? formatEndTime(weeklyRaffle?.results[0]?.end_time, true) : '--'}
              </Box>
            </Text>
            <Button
              variant="blue"
              mt="4px"
              h="40px"
              w="auto"
              px="20px"
              textTransform="uppercase"
              isDisabled={weeklyRaffle?.results?.[0]?.is_participating}
              _disabled={{
                backgroundColor: 'var(--chakra-colors-blue)',

                cursor: 'not-allowed',
              }}
              onClick={() => {
                event('Game', EVENTS.VIEW_RESULTS, 'view_raffle');
                dispatch(setInitialIndex(2));
                dispatch(setDisplayingStatus());
              }}
            >
              {weeklyRaffle?.results?.[0]?.is_participating ? (
                <Flex gap="4px" alignItems="center">
                  YOU'RE IN{' '}
                  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.916 3.37604C13.2607 3.60581 13.3538 4.07146 13.124 4.4161L8.12404 11.9161C7.9994 12.1031 7.7975 12.2243 7.5739 12.2464C7.3503 12.2686 7.12855 12.1893 6.96967 12.0304L3.96967 9.03041C3.67678 8.73752 3.67678 8.26264 3.96967 7.96975C4.26256 7.67686 4.73744 7.67686 5.03033 7.96975L7.38343 10.3229L11.876 3.58405C12.1057 3.23941 12.5714 3.14628 12.916 3.37604Z"
                      fill="white"
                      fillOpacity="0.7"
                    />
                  </svg>
                </Flex>
              ) : (
                'ENTER'
              )}
            </Button>
          </Flex>
          <Img
            flex="1"
            src={weeklyRaffle?.results?.[0]?.image ? weeklyRaffle?.results?.[0]?.image : '--'}
            alt="#"
            objectFit="cover"
            objectPosition="left center"
            zIndex="1"
            width="100%"
          />
        </>
      )}
    </Flex>
  );
};

export default RaffleBanner;
